import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CalendarArrow } from "../Icons/CalendarArrow";

import "@/assets/css/fullCalendar.scss";
import { updateSelectedCandidateDatesforPropose } from "@/reducers/clientunfilledcontracts";

const ProposeCandidateShiftsCalendar = ({ candidateId }) => {
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const { shiftsForProposeCandidate, selectedCandidateDatesforPropose } =
    useSelector((state) => state.clientUnfilledContracts);

  const selectedDates = selectedCandidateDatesforPropose[candidateId] || [];

  // 🧠 Determine starting month based on current group (Jan–Apr, May–Aug, Sep–Dec)
  const getStartMonthOfGroup = (monthIndex) => {
    if (monthIndex <= 3) return 0; // Jan–Apr
    if (monthIndex <= 7) return 4; // May–Aug
    return 8; // Sep–Dec
  };

  const firstDate = shiftsForProposeCandidate[0]?.contract_date;
  const firstMoment = moment(firstDate, "DD/MM/YYYY");
  const currentMonthIndex = firstMoment.month(); // 0-11
  const currentYear = firstMoment.year(); // <-- get the correct year

  const [visibleStartMonth, setVisibleStartMonth] = useState(
    moment()
      .year(currentYear) // set correct year
      .month(getStartMonthOfGroup(currentMonthIndex))
      .startOf("month"),
  );

  // 🧮 Helper: get 4 visible months in this block
  const getVisibleMonths = () =>
    Array.from({ length: 4 }, (_, i) =>
      moment(visibleStartMonth).add(i, "months"),
    );

  const visibleMonths = getVisibleMonths();

  // 🧭 Sync calendar when visibleStartMonth changes
  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.gotoDate(visibleStartMonth.toDate());
    }
  }, [visibleStartMonth]);

  // ⏩ Move forward/backward 4-month groups
  const handleNextClick = () =>
    setVisibleStartMonth((prev) => moment(prev).add(4, "months"));

  const handlePrevClick = () =>
    setVisibleStartMonth((prev) => moment(prev).subtract(4, "months"));

  // 🏷️ Smart label
  const lastMonth = visibleMonths[3];
  const label =
    visibleStartMonth.year() === lastMonth.year()
      ? `${visibleStartMonth.format("MMM")} – ${lastMonth.format("MMM YYYY")}`
      : `${visibleStartMonth.format("MMM YYYY")} – ${lastMonth.format("MMM YYYY")}`;

  // 🔁 Helper to hide non-visible months
  const hideNonVisibleMonths = () => {
    const months = document.querySelectorAll(".fc-multimonth-month");
    months.forEach((monthEl) => {
      const dateAttr = monthEl.getAttribute("data-date");
      if (!dateAttr) return;
      const monthDate = moment(dateAttr).startOf("month");
      const inVisibleRange = visibleMonths.some((m) =>
        m.isSame(monthDate, "month"),
      );
      monthEl.style.display = inVisibleRange ? "block" : "none";
    });
  };

  // 🧮 Define current visible 4-month range
  const visibleStart = moment(visibleStartMonth).startOf("month");
  const visibleEnd = moment(visibleStartMonth).add(3, "months").endOf("month");

  // 🧮 Count shifts before and after current range
  const prevCount = shiftsForProposeCandidate.filter((shift) => {
    const date = moment(shift.contract_date, "DD/MM/YYYY");
    return date.isBefore(visibleStart, "day");
  }).length;

  const nextCount = shiftsForProposeCandidate.filter((shift) => {
    const date = moment(shift.contract_date, "DD/MM/YYYY");
    return date.isAfter(visibleEnd, "day");
  }).length;
  return (
    <div className="ProposeCandidateShiftsCalendar_wrapper p-4">
      {/* ==== HEADER ==== */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={handlePrevClick}
          className="flex items-center rounded-lg bg-gray-200 px-3 py-2 transition-colors hover:bg-gray-300"
        >
          <CalendarArrow cls="mr-2 h-4 w-4 " />
          Previous &nbsp;
          {prevCount > 0 && <span className="text-primary">({prevCount})</span>}
        </button>

        <span className="text-lg font-semibold">{label}</span>

        <button
          onClick={handleNextClick}
          className="flex items-center rounded-lg bg-gray-200 px-3 py-2 transition-colors hover:bg-gray-300"
        >
          Next &nbsp;
          {nextCount > 0 && <span className="text-primary">({nextCount})</span>}
          <CalendarArrow cls="ml-2 h-4 w-4 rotate-180" />
        </button>
      </div>

      {/* ==== CALENDAR ==== */}
      <div className="fullcalendar_wrapper select-none overflow-y-auto scrollbar-hide">
        <FullCalendar
          ref={calendarRef}
          plugins={[multiMonthPlugin, interactionPlugin]}
          initialView="multiMonthYear"
          initialDate={visibleStartMonth.toDate()}
          multiMonthMaxColumns={2}
          multiMonthMinWidth={350}
          height="auto"
          selectable={true}
          dayHeaderContent={(args) => moment(args.date).format("dd")}
          dayCellClassNames={(args) => {
            const date = moment(args.date).format("DD/MM/YYYY");
            const isFixed = shiftsForProposeCandidate.some(
              (d) => d.contract_date === date,
            );
            const isSelected = selectedDates.some(
              (d) => d.contract_date === date,
            );

            if (isFixed && isSelected)
              return "!bg-yellow !text-white !border-0 !font-semibold !rounded-lg cursor-pointer";
            if (isFixed)
              return "!bg-yellow-300 !border-[2px] !border-yellow !font-semibold !rounded-lg cursor-pointer";
            return "cursor-not-allowed !font-medium !rounded-lg";
          }}
          dateClick={(info) => {
            const date = moment(info.dateStr, "YYYY-MM-DD").format(
              "DD/MM/YYYY",
            );
            const isFixed = shiftsForProposeCandidate.find(
              (d) => d.contract_date === date,
            );
            if (!isFixed) return;

            const isSelected = selectedDates.find(
              (d) => d.contract_date === date,
            );
            let updated = [...selectedDates];
            if (isSelected) {
              updated = updated.filter((d) => d.contract_date !== date);
            } else {
              updated.push(isFixed);
            }
            dispatch(
              updateSelectedCandidateDatesforPropose({
                [candidateId]: updated,
              }),
            );
          }}
          viewDidMount={hideNonVisibleMonths}
          datesSet={hideNonVisibleMonths}
        />
      </div>
    </div>
  );
};

export default ProposeCandidateShiftsCalendar;
